import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Car,
  Camera,
  Loader2,
  X,
  Star,
  Upload,
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  DollarSign,
  Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cloudinary config
const CLOUDINARY_CLOUD_NAME = 'dieapbotq';
const CLOUDINARY_UPLOAD_PRESET = 'sell-car-uploads';

// Limits
const MAX_IMAGES = 20;
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

// Get auth headers
const getAuthHeaders = () => {
  const token = sessionStorage.getItem('dashboard_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Fuel types and other options
const FUEL_TYPES = ['Petrol', 'Diesel', 'Electric', 'Hybrid', 'Plug-in Hybrid'];
const BODY_TYPES = ['Saloon', 'Hatchback', 'SUV', 'Estate', 'Coupe', 'Convertible', 'MPV', 'Van'];
const TRANSMISSIONS = ['Automatic', 'Manual', 'Semi-Automatic'];

// Image Upload Component
const ImageUploadZone = ({ images, setImages, uploading, setUploading, coverIndex, setCoverIndex }) => {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = async (files) => {
    const fileArray = Array.from(files);
    
    // Check total count
    if (images.length + fileArray.length > MAX_IMAGES) {
      toast.error(`Maximum ${MAX_IMAGES} images allowed. You can add ${MAX_IMAGES - images.length} more.`);
      return;
    }

    for (const file of fileArray) {
      // Validate type
      if (!ALLOWED_IMAGE_TYPES.includes(file.type.toLowerCase())) {
        toast.error(`${file.name}: Invalid file type. Use JPG, PNG, or WebP.`);
        continue;
      }

      // Validate size
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`${file.name}: File too large. Max ${MAX_FILE_SIZE_MB}MB.`);
        continue;
      }

      // Upload to Cloudinary
      const tempId = `temp_${Date.now()}_${Math.random()}`;
      setUploading(prev => ({ ...prev, [tempId]: true }));

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
        formData.append('folder', 'dashboard_cars');

        const response = await fetch(
          `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
          { method: 'POST', body: formData }
        );

        if (!response.ok) {
          throw new Error('Upload failed');
        }

        const data = await response.json();
        setImages(prev => [...prev, data.secure_url]);
        toast.success(`Image uploaded`);
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Failed to upload ${file.name}`);
      } finally {
        setUploading(prev => {
          const next = { ...prev };
          delete next[tempId];
          return next;
        });
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    // Adjust cover index if needed
    if (coverIndex === index) {
      setCoverIndex(0);
    } else if (coverIndex > index) {
      setCoverIndex(prev => prev - 1);
    }
  };

  const setCover = (index) => {
    setCoverIndex(index);
    toast.success('Cover image set');
  };

  const isUploading = Object.keys(uploading).length > 0;

  return (
    <div className="space-y-4">
      {/* Upload Zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
          dragOver
            ? 'border-[#48CCCD] bg-[#48CCCD]/10'
            : 'border-white/20 hover:border-[#48CCCD]/50 bg-[#0A0F14]/50'
        }`}
        data-testid="image-upload-zone"
      >
        {isUploading ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 size={32} className="text-[#48CCCD] animate-spin" />
            <p className="text-sm text-[#94A3B8]">Uploading...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <Upload size={32} className="text-[#94A3B8]" />
            <div>
              <p className="text-sm text-[#F8FAFC]">Drag & drop images or click to browse</p>
              <p className="text-xs text-[#64748B] mt-1">
                JPG, PNG, WebP • Max {MAX_FILE_SIZE_MB}MB each • Up to {MAX_IMAGES} images
              </p>
            </div>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
          multiple
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
        />
      </div>

      {/* Image Count */}
      <div className="flex items-center justify-between text-xs">
        <span className={`font-sans ${images.length > 0 ? 'text-[#48CCCD]' : 'text-[#94A3B8]'}`}>
          {images.length} / {MAX_IMAGES} images
        </span>
        {images.length > 0 && (
          <span className="text-[#94A3B8]">
            Click <Star size={12} className="inline text-yellow-400" /> to set cover image
          </span>
        )}
      </div>

      {/* Image Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {images.map((url, index) => (
            <div
              key={url}
              className={`relative aspect-[4/3] group ${
                index === coverIndex ? 'ring-2 ring-[#48CCCD]' : ''
              }`}
              data-testid={`image-preview-${index}`}
            >
              <img
                src={url}
                alt={`Car image ${index + 1}`}
                className="w-full h-full object-cover"
              />
              
              {/* Cover badge */}
              {index === coverIndex && (
                <div className="absolute top-1 left-1 bg-[#48CCCD] px-1.5 py-0.5 text-[10px] font-bold text-black uppercase">
                  Cover
                </div>
              )}

              {/* Action buttons */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                {index !== coverIndex && (
                  <button
                    type="button"
                    onClick={() => setCover(index)}
                    className="p-2 bg-yellow-500 text-black hover:bg-yellow-400"
                    title="Set as cover"
                  >
                    <Star size={16} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="p-2 bg-red-600 text-white hover:bg-red-500"
                  title="Remove"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DashboardAddCarPage = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState({});
  const [coverIndex, setCoverIndex] = useState(0);

  // Form state
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    trim: '',
    year: '',
    price: '',
    mileage: '',
    fuel_type: '',
    body_type: '',
    transmission: '',
    engine_size: '',
    colour: '',
    description: '',
    notes: '',
    featured: false,
    status: 'available',
    finance_enabled: false,
    default_deposit: '',
    interest_rate: ''
  });

  // Check auth on mount
  useEffect(() => {
    const token = sessionStorage.getItem('dashboard_token');
    if (!token) {
      navigate('/dashboard/login');
    }
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.make.trim()) newErrors.make = 'Make is required';
    if (!formData.model.trim()) newErrors.model = 'Model is required';
    if (!formData.year) newErrors.year = 'Year is required';
    else if (formData.year < 1900 || formData.year > 2100) newErrors.year = 'Invalid year';
    if (!formData.price) newErrors.price = 'Price is required';
    else if (formData.price < 0) newErrors.price = 'Price must be positive';
    if (!formData.mileage && formData.mileage !== 0) newErrors.mileage = 'Mileage is required';
    else if (formData.mileage < 0) newErrors.mileage = 'Mileage must be positive';
    if (!formData.fuel_type) newErrors.fuel_type = 'Fuel type is required';
    if (!formData.body_type) newErrors.body_type = 'Body type is required';
    if (!formData.transmission) newErrors.transmission = 'Transmission is required';
    if (images.length === 0) newErrors.images = 'At least 1 image is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Check if uploads are in progress
    if (Object.keys(uploading).length > 0) {
      toast.error('Please wait for image uploads to complete');
      return;
    }

    setIsSubmitting(true);

    try {
      // Reorder images so cover is first
      const orderedImages = [...images];
      if (coverIndex !== 0) {
        const [cover] = orderedImages.splice(coverIndex, 1);
        orderedImages.unshift(cover);
      }

      const payload = {
        make: formData.make.trim(),
        model: formData.model.trim(),
        trim: formData.trim.trim() || null,
        year: parseInt(formData.year),
        price: parseFloat(formData.price),
        mileage: parseInt(formData.mileage),
        fuel_type: formData.fuel_type,
        body_type: formData.body_type,
        transmission: formData.transmission,
        engine_size: formData.engine_size.trim() || null,
        colour: formData.colour.trim() || null,
        description: formData.description.trim() || null,
        notes: formData.notes.trim() || null,
        featured: formData.featured,
        status: formData.status,
        finance_enabled: formData.finance_enabled,
        default_deposit: formData.default_deposit ? parseFloat(formData.default_deposit) : null,
        interest_rate: formData.interest_rate ? parseFloat(formData.interest_rate) : null,
        images: orderedImages
      };

      const response = await fetch(`${API}/dashboard/cars`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(payload)
      });

      if (response.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/dashboard/login');
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create car');
      }

      toast.success(data.message || 'Car created successfully!');
      
      // Navigate to the car detail page
      navigate(`/cars/${data.car_id}`);
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020405] pt-20" data-testid="dashboard-add-car-page">
      {/* Header */}
      <section className="py-8 md:py-12 bg-gradient-to-br from-[#16202A] via-[#0A0F14] to-[#020405]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              size="sm"
              className="h-10 px-3 bg-transparent border border-white/20 text-[#94A3B8] hover:text-white hover:border-white/40"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft size={18} />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center bg-[#48CCCD]/20">
                <Car size={24} className="text-[#48CCCD]" />
              </div>
              <div>
                <h1 className="font-serif text-2xl md:text-3xl font-bold text-[#F8FAFC] tracking-tight">
                  Add New Car
                </h1>
                <p className="text-xs text-[#94A3B8] font-sans">Create a new car listing</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-8 md:py-12">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <form onSubmit={handleSubmit} className="space-y-8">

            {/* Section 1: Basic Details */}
            <div className="bg-[#16202A]/50 border border-white/10 p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-[#48CCCD]/20">
                  <Car size={20} className="text-[#48CCCD]" />
                </div>
                <div>
                  <h2 className="text-[#F8FAFC] font-serif text-xl font-bold">Vehicle Details</h2>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans">
                    Basic information
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Make */}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                    Make <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    name="make"
                    value={formData.make}
                    onChange={handleInputChange}
                    placeholder="e.g. BMW"
                    className={`bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-12 focus:border-[#48CCCD] ${errors.make ? 'border-red-500' : ''}`}
                    data-testid="input-make"
                  />
                  {errors.make && <p className="text-red-400 text-xs mt-1">{errors.make}</p>}
                </div>

                {/* Model */}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                    Model <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                    placeholder="e.g. 3 Series"
                    className={`bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-12 focus:border-[#48CCCD] ${errors.model ? 'border-red-500' : ''}`}
                    data-testid="input-model"
                  />
                  {errors.model && <p className="text-red-400 text-xs mt-1">{errors.model}</p>}
                </div>

                {/* Trim */}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                    Trim <span className="text-[#64748B]">(optional)</span>
                  </label>
                  <Input
                    type="text"
                    name="trim"
                    value={formData.trim}
                    onChange={handleInputChange}
                    placeholder="e.g. M Sport"
                    className="bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-12 focus:border-[#48CCCD]"
                    data-testid="input-trim"
                  />
                </div>

                {/* Year */}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                    Year <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="number"
                    name="year"
                    value={formData.year}
                    onChange={handleInputChange}
                    placeholder="e.g. 2020"
                    min="1900"
                    max="2100"
                    className={`bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-12 focus:border-[#48CCCD] ${errors.year ? 'border-red-500' : ''}`}
                    data-testid="input-year"
                  />
                  {errors.year && <p className="text-red-400 text-xs mt-1">{errors.year}</p>}
                </div>

                {/* Price */}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                    Price (€) <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="e.g. 25000"
                    min="0"
                    step="0.01"
                    className={`bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-12 focus:border-[#48CCCD] ${errors.price ? 'border-red-500' : ''}`}
                    data-testid="input-price"
                  />
                  {errors.price && <p className="text-red-400 text-xs mt-1">{errors.price}</p>}
                </div>

                {/* Mileage */}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                    Mileage (mi) <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="number"
                    name="mileage"
                    value={formData.mileage}
                    onChange={handleInputChange}
                    placeholder="e.g. 45000"
                    min="0"
                    className={`bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-12 focus:border-[#48CCCD] ${errors.mileage ? 'border-red-500' : ''}`}
                    data-testid="input-mileage"
                  />
                  {errors.mileage && <p className="text-red-400 text-xs mt-1">{errors.mileage}</p>}
                </div>

                {/* Fuel Type */}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                    Fuel Type <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={formData.fuel_type}
                    onValueChange={(value) => handleSelectChange('fuel_type', value)}
                  >
                    <SelectTrigger
                      className={`bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-12 ${errors.fuel_type ? 'border-red-500' : ''}`}
                      data-testid="select-fuel-type"
                    >
                      <SelectValue placeholder="Select fuel type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#16202A] border-white/10">
                      {FUEL_TYPES.map(type => (
                        <SelectItem key={type} value={type} className="text-[#F8FAFC]">{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.fuel_type && <p className="text-red-400 text-xs mt-1">{errors.fuel_type}</p>}
                </div>

                {/* Body Type */}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                    Body Type <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={formData.body_type}
                    onValueChange={(value) => handleSelectChange('body_type', value)}
                  >
                    <SelectTrigger
                      className={`bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-12 ${errors.body_type ? 'border-red-500' : ''}`}
                      data-testid="select-body-type"
                    >
                      <SelectValue placeholder="Select body type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#16202A] border-white/10">
                      {BODY_TYPES.map(type => (
                        <SelectItem key={type} value={type} className="text-[#F8FAFC]">{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.body_type && <p className="text-red-400 text-xs mt-1">{errors.body_type}</p>}
                </div>

                {/* Transmission */}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                    Transmission <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={formData.transmission}
                    onValueChange={(value) => handleSelectChange('transmission', value)}
                  >
                    <SelectTrigger
                      className={`bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-12 ${errors.transmission ? 'border-red-500' : ''}`}
                      data-testid="select-transmission"
                    >
                      <SelectValue placeholder="Select transmission" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#16202A] border-white/10">
                      {TRANSMISSIONS.map(type => (
                        <SelectItem key={type} value={type} className="text-[#F8FAFC]">{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.transmission && <p className="text-red-400 text-xs mt-1">{errors.transmission}</p>}
                </div>

                {/* Engine Size */}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                    Engine Size <span className="text-[#64748B]">(optional)</span>
                  </label>
                  <Input
                    type="text"
                    name="engine_size"
                    value={formData.engine_size}
                    onChange={handleInputChange}
                    placeholder="e.g. 2.0L"
                    className="bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-12 focus:border-[#48CCCD]"
                    data-testid="input-engine-size"
                  />
                </div>

                {/* Colour */}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                    Colour <span className="text-[#64748B]">(optional)</span>
                  </label>
                  <Input
                    type="text"
                    name="colour"
                    value={formData.colour}
                    onChange={handleInputChange}
                    placeholder="e.g. Black"
                    className="bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-12 focus:border-[#48CCCD]"
                    data-testid="input-colour"
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                    Status
                  </label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => handleSelectChange('status', value)}
                  >
                    <SelectTrigger
                      className="bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-12"
                      data-testid="select-status"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#16202A] border-white/10">
                      <SelectItem value="available" className="text-[#F8FAFC]">Available</SelectItem>
                      <SelectItem value="sold" className="text-[#F8FAFC]">Sold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Description */}
              <div className="mt-4">
                <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                  Description <span className="text-[#64748B]">(optional)</span>
                </label>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Full car description - features, history, condition..."
                  rows={4}
                  className="bg-[#0A0F14] border-white/10 text-[#F8FAFC] focus:border-[#48CCCD] resize-none"
                  data-testid="input-description"
                />
              </div>

              {/* Featured Checkbox */}
              <div className="mt-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="featured"
                    checked={formData.featured}
                    onChange={handleInputChange}
                    className="w-5 h-5 rounded bg-[#0A0F14] border-white/20 text-[#48CCCD] focus:ring-[#48CCCD]"
                    data-testid="checkbox-featured"
                  />
                  <span className="text-[#F8FAFC] font-sans text-sm flex items-center gap-2">
                    <Star size={16} className="text-yellow-400" />
                    Featured on Homepage
                  </span>
                </label>
              </div>
            </div>

            {/* Section 2: Images */}
            <div className="bg-[#16202A]/50 border border-white/10 p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-[#48CCCD]/20">
                  <Camera size={20} className="text-[#48CCCD]" />
                </div>
                <div>
                  <h2 className="text-[#F8FAFC] font-serif text-xl font-bold">Photos</h2>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans">
                    At least 1 required, up to {MAX_IMAGES}
                  </p>
                </div>
              </div>

              {errors.images && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 mb-4">
                  <AlertTriangle size={16} className="text-red-400" />
                  <p className="text-red-400 text-sm">{errors.images}</p>
                </div>
              )}

              <ImageUploadZone
                images={images}
                setImages={setImages}
                uploading={uploading}
                setUploading={setUploading}
                coverIndex={coverIndex}
                setCoverIndex={setCoverIndex}
              />
            </div>

            {/* Section 3: Finance Options */}
            <div className="bg-[#16202A]/50 border border-white/10 p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-[#48CCCD]/20">
                  <DollarSign size={20} className="text-[#48CCCD]" />
                </div>
                <div>
                  <h2 className="text-[#F8FAFC] font-serif text-xl font-bold">Finance Options</h2>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans">
                    Optional finance settings
                  </p>
                </div>
              </div>

              {/* Enable Finance */}
              <label className="flex items-center gap-3 cursor-pointer mb-4">
                <input
                  type="checkbox"
                  name="finance_enabled"
                  checked={formData.finance_enabled}
                  onChange={handleInputChange}
                  className="w-5 h-5 rounded bg-[#0A0F14] border-white/20 text-[#48CCCD] focus:ring-[#48CCCD]"
                  data-testid="checkbox-finance"
                />
                <span className="text-[#F8FAFC] font-sans text-sm">Enable finance enquiries for this car</span>
              </label>

              {formData.finance_enabled && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 pl-8 border-l-2 border-[#48CCCD]/30">
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                      Default Deposit (€)
                    </label>
                    <Input
                      type="number"
                      name="default_deposit"
                      value={formData.default_deposit}
                      onChange={handleInputChange}
                      placeholder="e.g. 2500"
                      min="0"
                      step="0.01"
                      className="bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-12 focus:border-[#48CCCD]"
                      data-testid="input-deposit"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                      Interest Rate (%)
                    </label>
                    <Input
                      type="number"
                      name="interest_rate"
                      value={formData.interest_rate}
                      onChange={handleInputChange}
                      placeholder="e.g. 8.9"
                      min="0"
                      max="100"
                      step="0.1"
                      className="bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-12 focus:border-[#48CCCD]"
                      data-testid="input-interest-rate"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Section 4: Internal Notes */}
            <div className="bg-[#16202A]/50 border border-white/10 p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-[#48CCCD]/20">
                  <Info size={20} className="text-[#48CCCD]" />
                </div>
                <div>
                  <h2 className="text-[#F8FAFC] font-serif text-xl font-bold">Internal Notes</h2>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans">
                    Not shown publicly
                  </p>
                </div>
              </div>

              <Textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Private notes for staff only..."
                rows={3}
                className="bg-[#0A0F14] border-white/10 text-[#F8FAFC] focus:border-[#48CCCD] resize-none"
                data-testid="input-notes"
              />
            </div>

            {/* Submit */}
            <div className="flex flex-col sm:flex-row gap-4 justify-end">
              <Button
                type="button"
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="h-12 px-8 bg-transparent border border-white/20 text-[#94A3B8] hover:text-white hover:border-white/40 font-sans text-xs uppercase tracking-wider"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || Object.keys(uploading).length > 0}
                className="h-12 px-8 bg-[#48CCCD] text-black font-bold uppercase tracking-widest text-xs hover:bg-white transition-colors disabled:opacity-50"
                data-testid="submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={18} className="mr-2" />
                    Create Car
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default DashboardAddCarPage;
